import { useState } from 'react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { PaperPlaneTilt, CaretDown, CaretRight } from '@phosphor-icons/react';
import { createIssueNote } from '../lib/api';
import { toast } from 'sonner';

interface InternalNote {
  id: string;
  text: string;
  author_name?: string;
  created_at: string;
}

interface EmailMessage {
  id: string;
  direction: string;
  body?: string;
  from_email?: string;
  created_at: string;
}

interface EmailThread {
  messages?: EmailMessage[];
}

interface SupplierMessage {
  id: string;
  text: string;
  created_at: string;
}

interface Order {
  subcontractor_name?: string;
  supplier_messages?: SupplierMessage[];
}

interface Issue {
  id: string;
  description?: string;
  reporter_name?: string;
  reporter_email?: string;
  created_at?: string;
  internal_notes?: InternalNote[];
}

interface TimelineItem {
  id: string;
  type: string;
  text?: string;
  author: string;
  timestamp: string;
  color: 'white' | 'blue' | 'green' | 'yellow';
}

interface IssueConversationProps {
  issue: Issue;
  emailThread?: EmailThread;
  order?: Order;
  onNoteAdded?: (note: InternalNote) => void;
  showNoteInput?: boolean;
}

/**
 * IssueConversation - Displays conversation flow with color-coded messages
 * 
 * Message types and colors:
 * - BLUE (slate-800): Outbound emails from support
 * - WHITE/LIGHT: Inbound emails from reporter
 * - GREEN (emerald): Internal notes (only visible to staff)
 * - YELLOW (amber): Supplier messages from external order page
 */
export default function IssueConversation({ 
  issue, 
  emailThread, 
  order,
  onNoteAdded,
  showNoteInput = true 
}: IssueConversationProps) {
  const [noteText, setNoteText] = useState('');
  const [sending, setSending] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});

  const toggleExpanded = (id: string) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Combine all messages into one timeline
  const buildTimeline = (): TimelineItem[] => {
    const items: TimelineItem[] = [];

    // Find the first inbound email timestamp for description (if email-sourced)
    let descriptionTimestamp = issue?.created_at || new Date().toISOString();
    if (emailThread?.messages?.length) {
      const firstInbound = emailThread.messages.find(m => m.direction === 'inbound');
      if (firstInbound) {
        descriptionTimestamp = firstInbound.created_at;
      }
    }

    // Add issue description as first item (if exists)
    if (issue?.description) {
      items.push({
        id: 'issue-description',
        type: 'inbound',
        text: issue.description,
        author: issue.reporter_name || issue.reporter_email?.split('@')[0] || 'Anmälare',
        timestamp: descriptionTimestamp,
        color: 'white'
      });
    }

    // Add email messages (but skip first inbound if it matches issue description)
    if (emailThread?.messages) {
      emailThread.messages.forEach((msg) => {
        // Skip first inbound message if it matches description (avoid duplicate)
        if (msg.direction === 'inbound' && issue?.description) {
          const descClean = issue.description.trim().substring(0, 100);
          const bodyClean = (msg.body || '').trim().substring(0, 100);
          if (descClean === bodyClean) {
            return; // Skip this message, it's the same as description
          }
        }
        
        items.push({
          id: msg.id,
          type: msg.direction === 'outbound' ? 'outbound' : 'inbound',
          text: msg.body,
          author: msg.direction === 'outbound' 
            ? 'Support' 
            : (msg.from_email?.split('@')[0] || 'Avsändare'),
          timestamp: msg.created_at,
          color: msg.direction === 'outbound' ? 'blue' : 'white'
        });
      });
    }

    // Internal notes (GREEN)
    if (issue?.internal_notes) {
      issue.internal_notes.forEach(note => {
        items.push({
          id: note.id,
          type: 'internal_note',
          text: note.text,
          author: note.author_name || 'Intern',
          timestamp: note.created_at,
          color: 'green'
        });
      });
    }

    // Supplier messages (YELLOW)
    if (order?.supplier_messages) {
      order.supplier_messages.forEach(msg => {
        items.push({
          id: msg.id,
          type: 'supplier',
          text: msg.text,
          author: order.subcontractor_name || 'Leverantör',
          timestamp: msg.created_at,
          color: 'yellow'
        });
      });
    }

    // Sort by timestamp
    items.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

    return items;
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    
    setSending(true);
    try {
      const { data } = await createIssueNote(issue.id, { text: noteText });
      setNoteText('');
      toast.success('Intern anteckning tillagd');
      if (onNoteAdded) onNoteAdded(data);
    } catch {
      toast.error('Kunde inte lägga till anteckning');
    } finally {
      setSending(false);
    }
  };

  const timeline = buildTimeline();

  const getMessageStyle = (color: string): string => {
    const base = 'transition-all duration-200 ';
    switch (color) {
      case 'blue':
        return base + 'bg-slate-800 text-white ml-auto';
      case 'white':
        return base + 'bg-white border border-slate-200 shadow-sm';
      case 'green':
        return base + 'bg-emerald-100 border border-emerald-300 text-emerald-900';
      case 'yellow':
        return base + 'bg-amber-100 border border-amber-300 text-amber-900';
      default:
        return base + 'bg-slate-100';
    }
  };

  const getAuthorStyle = (color: string): string => {
    switch (color) {
      case 'blue':
        return 'text-slate-300';
      case 'green':
        return 'text-emerald-600';
      case 'yellow':
        return 'text-amber-600';
      default:
        return 'text-slate-500';
    }
  };

  const getTypeLabel = (type: string): string => {
    switch (type) {
      case 'internal_note':
        return '(Intern)';
      case 'supplier':
        return '(Leverantör)';
      default:
        return '';
    }
  };

  const truncateText = (text: string | undefined, maxLength: number = 80): string => {
    if (!text) return '';
    const cleaned = text.replace(/\s+/g, ' ').trim();
    if (cleaned.length <= maxLength) return cleaned;
    return cleaned.substring(0, maxLength) + '...';
  };

  return (
    <div className="space-y-4">
      {/* Timeline - Mobile Responsive */}
      <div className="space-y-2 max-h-[400px] sm:max-h-[500px] overflow-y-auto p-1 sm:p-2">
        {timeline.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-8">Ingen konversation ännu</p>
        ) : (
          timeline.map((item) => {
            const isExpanded = expandedItems[item.id];
            const needsTruncation = item.text && item.text.length > 50;
            
            return (
              <div 
                key={item.id} 
                className={`flex ${item.color === 'blue' ? 'justify-end' : 'justify-start'}`}
              >
                <div 
                  className={`max-w-[95%] sm:max-w-[85%] rounded-lg cursor-pointer ${getMessageStyle(item.color)} ${isExpanded ? 'p-2 sm:p-3' : 'p-1.5 sm:p-2 px-2 sm:px-3'}`}
                  onClick={() => needsTruncation && toggleExpanded(item.id)}
                >
                  {/* Header row - always visible */}
                  <div className={`flex items-center gap-1 sm:gap-2 text-[10px] sm:text-xs ${getAuthorStyle(item.color)} flex-wrap`}>
                    {needsTruncation && (
                      <span className="flex-shrink-0">
                        {isExpanded ? (
                          <CaretDown size={12} weight="bold" />
                        ) : (
                          <CaretRight size={12} weight="bold" />
                        )}
                      </span>
                    )}
                    <span className="font-medium">{item.author}</span>
                    <span className="opacity-70">{getTypeLabel(item.type)}</span>
                    <span className="opacity-50">
                      {new Date(item.timestamp).toLocaleString('sv-SE', {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                  
                  {/* Content - truncated or full */}
                  <div className={`text-xs sm:text-sm mt-1 ${!isExpanded && needsTruncation ? '' : 'whitespace-pre-wrap break-words'}`}>
                    {isExpanded || !needsTruncation 
                      ? item.text 
                      : truncateText(item.text?.replace(/\n/g, ' '), 80)}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Internal note input - Mobile Responsive */}
      {showNoteInput && (
        <div className="border-t border-slate-200 pt-3 sm:pt-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2.5 sm:w-3 h-2.5 sm:h-3 rounded-full bg-emerald-500"></div>
            <span className="text-[10px] sm:text-xs font-medium text-emerald-700">Lägg till intern anteckning</span>
          </div>
          <div className="flex gap-2">
            <Textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Skriv en intern anteckning (syns endast för personal)..."
              className="min-h-[50px] sm:min-h-[60px] text-xs sm:text-sm bg-emerald-50 border-emerald-200 focus:border-emerald-400"
            />
            <Button
              onClick={handleAddNote}
              disabled={!noteText.trim() || sending}
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 self-end h-8 sm:h-9 px-2 sm:px-3"
            >
              <PaperPlaneTilt size={16} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
